import { forwardRef, type ChangeEvent } from 'react'
import { CopyInputValueButton } from './copy-input-value-button'
import { FormInputGroup, type FormInputGroupProps } from './form-input'
import { Loader } from './loader'

interface FormUrlPathInputProps extends Omit<
  FormInputGroupProps,
  'leadingIcon' | 'prefix' | 'suffix' | 'trailingAction'
> {
  additionalPath?: string
  domainName?: string
}

const getUrlPathPrefix = (
  scheme: string | undefined,
  domainName?: string,
  additionalPath?: string,
) => {
  if (!domainName) return `${scheme}://`
  return `${scheme}://${domainName}/${additionalPath ? `${additionalPath}/` : ''}`
}

const sanitizeUrlPath = (value: string) => value.replace(/^\/+/, '')

const FormUrlPathInput = forwardRef<HTMLInputElement, FormUrlPathInputProps>(
  (
    { additionalPath, domainName, isPreFetching, onChange, statusIcon, value, ...inputProps },
    ref,
  ) => {
    const scheme = process.env.NEXT_PUBLIC_SIO_HOST_SCHEME
    const urlPrefix = getUrlPathPrefix(scheme, domainName, additionalPath)

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
      if (!onChange) return

      onChange({
        ...event,
        target: { ...event.target, value: sanitizeUrlPath(event.target.value) },
      })
    }

    return (
      <FormInputGroup
        {...inputProps}
        ref={ref}
        isPreFetching={isPreFetching}
        inputClassName="field-sizing-content min-w-10 max-w-full flex-auto"
        onChange={handleChange}
        prefix={
          !domainName && isPreFetching ? (
            <Loader small />
          ) : (
            <span className="truncate">{urlPrefix}</span>
          )
        }
        prefixClassName="min-w-0"
        spellCheck={false}
        statusIcon={statusIcon}
        trailingAction={
          <CopyInputValueButton disabled={isPreFetching} text={`${urlPrefix}${value ?? ''}`} />
        }
        type="text"
        value={value}
      />
    )
  },
)

FormUrlPathInput.displayName = 'FormUrlPathInput'

export { FormUrlPathInput, getUrlPathPrefix, sanitizeUrlPath }
export type { FormUrlPathInputProps }
